import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Upload, FileText, Trash2, Download, Shield, Receipt as ReceiptIcon, Home, Folder } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const CATEGORIES = [
  { value: 'will', label: 'Will / Estate', icon: Shield },
  { value: 'insurance', label: 'Insurance', icon: Shield },
  { value: 'tax', label: 'Tax', icon: ReceiptIcon },
  { value: 'property', label: 'Property', icon: Home },
  { value: 'legal', label: 'Legal', icon: FileText },
  { value: 'other', label: 'Other', icon: Folder },
];

const fmtBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const iconFor = (cat) => (CATEGORIES.find((c) => c.value === cat) || CATEGORIES[5]).icon;
const labelFor = (cat) => (CATEGORIES.find((c) => c.value === cat) || CATEGORIES[5]).label;

const WealthDocuments = () => {
  const [docs, setDocs] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('will');
  const [beneficiary, setBeneficiary] = useState('');
  const [linkedGoals, setLinkedGoals] = useState([]);

  const load = useCallback(async () => {
    try {
      const [d, g] = await Promise.all([
        axios.get(`${API}/documents`),
        axios.get(`${API}/goals`),
      ]);
      setDocs(d.data);
      setGoals(g.data);
    } catch (e) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleGoal = (gid) =>
    setLinkedGoals((prev) => (prev.includes(gid) ? prev.filter((x) => x !== gid) : [...prev, gid]));

  const upload = async () => {
    if (!file) {
      toast.error('Pick a file first.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', name || file.name);
      fd.append('category', category);
      fd.append('beneficiary', beneficiary);
      fd.append('linked_goal_ids', linkedGoals.join(','));
      await axios.post(`${API}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      setFile(null);
      setName('');
      setBeneficiary('');
      setLinkedGoals([]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const download = async (id, name) => {
    try {
      const res = await axios.get(`${API}/documents/${id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/documents/${id}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const goalName = (gid) => goals.find((g) => g.id === gid)?.name || '';

  if (loading) return <div className="p-8 md:p-12 font-chivo text-xl" data-testid="documents-loading">Loading documents…</div>;

  return (
    <div className="p-8 md:p-12" data-testid="wealth-documents-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="mb-8">
          <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight">Document vault</h1>
          <p className="text-slate-600 mt-1 font-ibm">Wills, insurance, tax returns and deeds — kept with the right beneficiaries and goals.</p>
        </div>

        {/* Upload form */}
        <div className="mb-6 border border-slate-200 bg-slate-50 rounded-sm p-6" data-testid="document-upload-form">
          <h2 className="text-sm font-chivo font-bold uppercase tracking-widest text-slate-900 mb-4">Upload new document</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.txt" data-testid="document-file" />
            <Input placeholder="Document name (optional)" value={name} onChange={(e) => setName(e.target.value)} data-testid="document-name" />
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="document-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Beneficiary (optional, e.g. 'Spouse', 'Child')" value={beneficiary} onChange={(e) => setBeneficiary(e.target.value)} data-testid="document-beneficiary" />
          </div>
          {goals.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-ibm font-semibold uppercase tracking-widest text-slate-600 mb-2">Link to goals (optional)</p>
              <div className="flex flex-wrap gap-2">
                {goals.map((g) => {
                  const linked = linkedGoals.includes(g.id);
                  return (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => toggleGoal(g.id)}
                      className={`px-3 py-1.5 text-xs font-ibm rounded-sm border ${linked ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-300 text-slate-700'}`}
                      data-testid={`document-link-goal-${g.id}`}
                    >
                      {g.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 mt-4">
            <Button onClick={upload} disabled={uploading || !file} className="bg-slate-900 text-white rounded-sm hover:bg-slate-800" data-testid="upload-document-btn">
              <Upload size={14} className="mr-2" />{uploading ? 'Uploading…' : 'Upload'}
            </Button>
            <p className="text-xs font-ibm text-slate-500">Max 25MB · PDF, images, Word, text</p>
          </div>
        </div>

        {/* Documents list */}
        {docs.length === 0 ? (
          <div className="border border-dashed border-slate-300 rounded-sm p-12 text-center" data-testid="no-documents">
            <FileText size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="font-ibm text-slate-600">No documents yet. Upload your first one above.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="documents-list">
            {docs.map((d) => {
              const Icon = iconFor(d.category);
              return (
                <div key={d.id} className="flex items-center justify-between gap-4 p-4 border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`document-row-${d.id}`}>
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-sm bg-slate-100 text-slate-900 flex items-center justify-center flex-shrink-0">
                      <Icon size={16} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-ibm font-medium text-slate-900 truncate">{d.name}</p>
                      <p className="text-xs text-slate-500 font-ibm mt-0.5">
                        <span className="uppercase tracking-widest">{labelFor(d.category)}</span>
                        {d.beneficiary && <span> · For {d.beneficiary}</span>}
                        <span> · {fmtBytes(d.file_size)}</span>
                        <span> · {new Date(d.uploaded_at).toLocaleDateString()}</span>
                      </p>
                      {d.linked_goal_ids?.length > 0 && (
                        <p className="text-xs text-slate-500 font-ibm mt-1">
                          Linked: {d.linked_goal_ids.map((gid) => goalName(gid)).filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => download(d.id, d.name)} data-testid={`download-${d.id}`}>
                      <Download size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => del(d.id)} data-testid={`delete-${d.id}`}>
                      <Trash2 size={14} className="text-red-600" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default WealthDocuments;
